import { useState } from 'react';
import Dexie from 'dexie';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  ZONA_HORARIA,
  centavos,
  fechaEnNicaragua,
  formatFxRate,
  formatMoney,
  fxRate,
  hoy,
  parseMoney,
  usdToCordobas,
  CUENTAS_BANCO,
  esCuentaEnDolares,
  type Centavos,
  type CuentaBanco,
  type Settings,
} from '@abastece/shared';
import { ACCOUNTS, alRecibir, yaContada, type Deposito, type SesionCaja, type TransferenciaBanco } from '@abastece/core';
import { db } from '../db/dexie.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { localReader, readSettings } from '../db/reader.js';
import { cuentasEnUso, nombreDeCuenta, nombrePuesto, saldosDeBancos } from '../lib/bancos.js';
import { saldoDelEstadoEnDolares } from '../lib/cuadrar.js';
import { emitirYVer } from '../lib/emitir.js';
import { localeActual, useT } from '../i18n/index.js';
import { Campo } from '../ui/Campo.js';
import { AnularDeposito, AnularTransferenciaBanco, ExplicarFaltante, faltanteDelConteo } from '../ui/Correcciones.js';
import { fechaCorta } from '../ui/CuentasDeCaja.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Modal } from '../ui/Modal.js';
import { Pagina } from '../ui/Pagina.js';
import { FilaDeposito, FilaTransferenciaBanco } from '../ui/FilasDeDinero.js';
import { VerTodos } from '../ui/FiltrosHistorial.js';
import { enRango } from '../lib/costos.js';
import { pagosEnCola } from './Transferencias.js';

const enDolares = (c: number) => `US$ ${formatMoney(centavos(c), { symbol: false })}`;
/** The bank accounts' are read on their own (`saldosDeBancos`), with a dollar account's dollars. */
const CUENTAS = [ACCOUNTS.CAJA_FUERTE.code, ACCOUNTS.EFECTIVO_POR_RECIBIR.code, ACCOUNTS.BILLETERA_MOVIL.code] as const;

/**
 * The latest ten of a list, and ten more at each «Ver más»: the deposits have a page of their own
 * («Ver todos»), what came back from the bank and what went between accounts have only this.
 */
const DE_A_DIEZ = 10;

/**
 * «Efectivo»: the shop's money once it is out of the drawer, for whoever holds the safe.
 *
 * What a till handed over waits here until it is received — counted blind, like the corte, and
 * whatever is short is that till's. General cash is counted against the books, and the bank and
 * the wallet against their statements. Until this existed none of it was ever checked: a retiro
 * was general cash the moment the cashier said so, and the safe was never counted at all.
 */
export function Efectivo() {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const saldos = useLiveQuery(
    async () => {
      const s: Record<string, number> = {};
      // Each account's days added up (`SaldoDiario`), not every entry the shop ever made.
      for (const cuenta of CUENTAS) {
        await db.saldosDiarios
          .where('[cuenta+fecha]')
          .between([cuenta, Dexie.minKey], [cuenta, Dexie.maxKey])
          .each((d) => {
            s[cuenta] = (s[cuenta] ?? 0) + d.debe - d.haber;
          });
      }
      return s;
    },
    [],
    {} as Record<string, number>,
  );
  const pendientes = useLiveQuery(
    () => db.sesiones.filter((s) => (s.porRecibir ?? 0) > 0 || (s.porRecibirDolares ?? 0) > 0).toArray(),
    [],
    [],
  );
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  /** What each bank account holds by the books, a dollar account's dollars too. */
  const bancos = useLiveQuery(() => saldosDeBancos(CUENTAS_BANCO), [], {} as Awaited<ReturnType<typeof saldosDeBancos>>);
  /**
   * The till being received, as it was when its sheet opened. Kept whole rather than looked up:
   * once received it has nothing pending and drops out of the list, and the sheet still has the
   * result to show.
   */
  const [recibiendo, setRecibiendo] = useState<SesionCaja | null>(null);
  const [contando, setContando] = useState(false);
  const [conciliando, setConciliando] = useState<CuentaBanco | 'BILLETERA_MOVIL' | null>(null);
  /** The latest deposits, for one that was never made to be voided. */
  const depositos = useLiveQuery(() => db.depositos.orderBy('fecha').reverse().filter((d) => !d.delBanco).limit(10).toArray(), [], []);
  /** And the cash brought back from the bank (`efectivo.sacado_del_banco`), kept with them, the latest first. */
  const sacados = useLiveQuery(() => db.depositos.orderBy('fecha').reverse().filter((d) => d.delBanco === true).toArray(), [], []);
  const [cuantosSacados, setCuantosSacados] = useState(DE_A_DIEZ);
  const [anulando, setAnulando] = useState<Deposito | null>(null);
  /** And the money moved from one bank account to another (`banco.transferido`), the latest first. */
  const entreCuentas = useLiveQuery(() => db.transferenciasBanco.orderBy('fecha').reverse().toArray(), [], []);
  const [cuantasEntreCuentas, setCuantasEntreCuentas] = useState(DE_A_DIEZ);
  const [anulandoTransferencia, setAnulandoTransferencia] = useState<TransferenciaBanco | null>(null);
  /**
   * The counts of general cash that came out short and are not all explained, the latest first:
   * one that was an expense nobody wrote down is explained here, whenever it comes to light.
   */
  const faltantes = useLiveQuery(
    async () => {
      const sinEfecto = await leerSinEfecto();
      const arqueos = (await db.eventos.where('type').equals('efectivo.arqueo').toArray()).filter((e) => tuvoEfecto(e, sinEfecto));
      const filas: { eventoId: string; fecha: string; queda: Centavos; explicado: Centavos }[] = [];
      for (const e of arqueos) {
        const { falto, explicado, fecha } = await faltanteDelConteo(e.id);
        if (falto > explicado) {
          filas.push({ eventoId: e.id, fecha: fecha ?? fechaEnNicaragua(e.clientTs), queda: centavos(falto - explicado), explicado });
        }
      }
      return filas.sort((a, b) => b.fecha.localeCompare(a.fecha));
    },
    [],
    [],
  );
  const [cuantosFaltantes, setCuantosFaltantes] = useState(DE_A_DIEZ);
  const [explicando, setExplicando] = useState<string | null>(null);

  const saldo = (codigo: string) => centavos(saldos[codigo] ?? 0);
  const nombre = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');
  const porRecibir = saldo(ACCOUNTS.EFECTIVO_POR_RECIBIR.code);
  const ordenados = [...pendientes].sort((a, b) => a.abiertaEn.localeCompare(b.abiertaEn));

  return (
    <Pagina titulo={t('efectivo.titulo')} subtitulo={t('efectivo.subtitulo')} volverA="/">
      <div className="flex flex-col gap-4">
        {/* The week's going over, whenever the owner wants it: the envelopes, the safe, the bank. */}
        <Link to="/dinero/semana" className="tarjeta-accion flex items-center gap-3 p-3.5">
          <Chip icono="ganancia" tinte="esmeralda" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{t('cuadrar.titulo')}</span>
            <span className="block text-xs text-slate-400">{t('cuadrar.desdeEfectivo')}</span>
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
        </Link>
        <section className="tarjeta">
          <header className="mb-2 flex items-center gap-3">
            <Chip icono="ingresar" tinte="ambar" />
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">{t('efectivo.porRecibir')}</h2>
              <p className="text-xs text-slate-500">{t('efectivo.porRecibirAyuda')}</p>
            </div>
          </header>
          {ordenados.length === 0 ? (
            <p className="py-2 text-sm text-slate-400">{t('efectivo.nadaPorRecibir')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-800">
              {ordenados.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    data-sin-icono="fila"
                    onClick={() => setRecibiendo(s)}
                    className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-3 text-left transition hover:bg-white/5"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">
                        {t('efectivo.cajaDe', {
                          nombre: nombre(s.usuarioId),
                          fecha: new Date(s.abiertaEn).toLocaleDateString(localeActual(), {
                            dateStyle: 'medium',
                            timeZone: ZONA_HORARIA,
                          }),
                        })}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {t(s.estado === 'abierta' ? 'efectivo.turnoAbierto' : 'efectivo.turnoCerrado')}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-right font-semibold">
                      {formatMoney(centavos(s.porRecibir ?? 0))}
                      {(s.porRecibirDolares ?? 0) > 0 && (
                        <span className="block text-sm font-normal text-slate-300">+ {enDolares(s.porRecibirDolares!)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="tarjeta">
          <header className="mb-3 flex items-center gap-3">
            <Chip icono="caja" tinte="esmeralda" />
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">{t('efectivo.general')}</h2>
              <p className="text-xs text-slate-500">{t('efectivo.generalAyuda')}</p>
            </div>
          </header>
          <Fila etiqueta={t('efectivo.segunLibros')} valor={formatMoney(saldo(ACCOUNTS.CAJA_FUERTE.code))} fuerte />
          <button type="button" className="btn-secundario mt-3 w-full" onClick={() => setContando(true)}>
            <Ico icono="contar" />
            {t('efectivo.contar')}
          </button>
          {faltantes.length > 0 && (
            <div className="mt-4 border-t border-slate-800 pt-3" data-efectivo-faltantes>
              <p className="text-sm font-medium text-rose-200">{t('efectivo.faltantes')}</p>
              <p className="text-xs text-slate-500">{t('efectivo.faltantesAyuda')}</p>
              <ul className="mt-1 flex flex-col divide-y divide-slate-800">
                {faltantes.slice(0, cuantosFaltantes).map((f) => (
                  <li key={f.eventoId} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0">
                      {fechaCorta(f.fecha)}
                      {f.explicado > 0 && (
                        <span className="block text-xs text-slate-500">{t('explicar.explicado', { monto: formatMoney(f.explicado) })}</span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="tabular text-rose-300">−{formatMoney(f.queda)}</span>
                      <button type="button" className="btn-texto gap-1 text-xs text-slate-400" onClick={() => setExplicando(f.eventoId)}>
                        <Ico icono="gastos" className="size-3.5" />
                        {t('explicar.boton')}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              {faltantes.length > cuantosFaltantes && (
                <button type="button" className="btn-secundario mt-3 w-full text-sm" onClick={() => setCuantosFaltantes((n) => n + DE_A_DIEZ)} data-efectivo-faltantes-mas>
                  <Ico icono="desplegar" />
                  {t('comun.verMas')}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="tarjeta">
          <header className="mb-3 flex items-center gap-3">
            <Chip icono="transferencias" tinte="cielo" />
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">{t('efectivo.bancoBilletera')}</h2>
              <p className="text-xs text-slate-500">{t('efectivo.bancoBilleteraAyuda')}</p>
            </div>
          </header>
          {/* Each account squared with its own statement; a dollar account's in dollars, and what the books carry them at. */}
          {cuentasEnUso(config, bancos).map((c) => {
            const s = bancos[c.cuenta] ?? { cordobas: centavos(0), dolares: centavos(0) };
            return (
              <div
                key={c.cuenta}
                className="flex items-center gap-3 py-2"
                data-efectivo-cuenta={c.cuenta}
                {...(c.cuenta === 'BANCO_DOLARES' ? { 'data-efectivo-banco-dolares': '' } : {})}
              >
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm text-slate-400">
                    {nombreDeCuenta(config, c.cuenta, t)}
                    {!c.activa && ` · ${t('bancos.cerrada')}`}
                  </span>
                  {/* Each figure kept whole when the line wraps, never «C$» on one line and its number on the next. */}
                  <span className="tabular block font-semibold">
                    {c.moneda === 'USD' && <span className="whitespace-nowrap">{enDolares(s.dolares)} ·</span>}
                    {c.moneda === 'USD' && ' '}
                    <span className="whitespace-nowrap">{formatMoney(s.cordobas)}</span>
                  </span>
                </span>
                <button type="button" className="btn-secundario shrink-0 px-4 text-sm" onClick={() => setConciliando(c.cuenta)}>
                  <Ico icono="calcular" className="size-4" />
                  {t('efectivo.conciliar')}
                </button>
              </div>
            );
          })}
          <div className="flex items-center gap-3 py-2" data-efectivo-cuenta="BILLETERA_MOVIL">
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-slate-400">{t('efectivo.billetera')}</span>
              <span className="tabular block font-semibold">{formatMoney(saldo(ACCOUNTS.BILLETERA_MOVIL.code))}</span>
            </span>
            <button type="button" className="btn-secundario shrink-0 px-4 text-sm" onClick={() => setConciliando('BILLETERA_MOVIL')}>
              <Ico icono="calcular" className="size-4" />
              {t('efectivo.conciliar')}
            </button>
          </div>
        </section>

        {depositos.length > 0 && (
          <section className="tarjeta" data-efectivo-depositos>
            <header className="mb-2 flex items-center gap-3">
              <Chip icono="transferencias" tinte="cielo" />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{t('efectivo.depositos')}</h2>
                <p className="text-xs text-slate-500">{t('efectivo.depositosAyuda')}</p>
              </div>
              <VerTodos to="/efectivo/depositos" />
            </header>
            <ul className="flex flex-col divide-y divide-slate-800">
              {depositos.map((d) => (
                <FilaDeposito key={d.id} deposito={d} config={config} onAnular={() => setAnulando(d)} />
              ))}
            </ul>
          </section>
        )}

        {sacados.length > 0 && (
          <section className="tarjeta" data-efectivo-del-banco>
            <header className="mb-2 flex items-center gap-3">
              <Chip icono="retirar" tinte="cielo" />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{t('efectivo.delBanco')}</h2>
                <p className="text-xs text-slate-500">{t('efectivo.delBancoAyuda')}</p>
              </div>
            </header>
            <ul className="flex flex-col divide-y divide-slate-800">
              {sacados.slice(0, cuantosSacados).map((d) => (
                <FilaDeposito key={d.id} deposito={d} config={config} onAnular={() => setAnulando(d)} />
              ))}
            </ul>
            {sacados.length > cuantosSacados && (
              <button type="button" className="btn-secundario mt-3 w-full text-sm" onClick={() => setCuantosSacados((n) => n + DE_A_DIEZ)} data-efectivo-del-banco-mas>
                <Ico icono="desplegar" />
                {t('comun.verMas')}
              </button>
            )}
          </section>
        )}

        {entreCuentas.length > 0 && (
          <section className="tarjeta" data-efectivo-entre-cuentas>
            <header className="mb-2 flex items-center gap-3">
              <Chip icono="banco" tinte="cielo" />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{t('efectivo.entreCuentas')}</h2>
                <p className="text-xs text-slate-500">{t('efectivo.entreCuentasAyuda')}</p>
              </div>
            </header>
            <ul className="flex flex-col divide-y divide-slate-800">
              {entreCuentas.slice(0, cuantasEntreCuentas).map((x) => (
                <FilaTransferenciaBanco key={x.id} transferencia={x} config={config} onAnular={() => setAnulandoTransferencia(x)} />
              ))}
            </ul>
            {entreCuentas.length > cuantasEntreCuentas && (
              <button type="button" className="btn-secundario mt-3 w-full text-sm" onClick={() => setCuantasEntreCuentas((n) => n + DE_A_DIEZ)} data-efectivo-entre-cuentas-mas>
                <Ico icono="desplegar" />
                {t('comun.verMas')}
              </button>
            )}
          </section>
        )}
      </div>

      {anulando && <AnularDeposito deposito={anulando} onCerrar={() => setAnulando(null)} />}
      {anulandoTransferencia && (
        <AnularTransferenciaBanco transferencia={anulandoTransferencia} onCerrar={() => setAnulandoTransferencia(null)} />
      )}
      {explicando && <ExplicarFaltante eventoId={explicando} onCerrar={() => setExplicando(null)} />}
      {recibiendo && (
        <Recibir
          sesion={recibiendo}
          tasa={config.fxTasaOficial}
          nombre={nombre(recibiendo.usuarioId)}
          onCerrar={() => setRecibiendo(null)}
        />
      )}
      {contando && (
        <Arqueo
          esperado={saldo(ACCOUNTS.CAJA_FUERTE.code)}
          porRecibir={porRecibir}
          tasa={config.fxTasaOficial}
          onCerrar={() => setContando(false)}
        />
      )}
      {conciliando && <Conciliar cuenta={conciliando} config={config} onCerrar={() => setConciliando(null)} />}
    </Pagina>
  );
}

function Fila({ etiqueta, valor, fuerte, tono }: { etiqueta: string; valor: string; fuerte?: boolean; tono?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-slate-400">{etiqueta}</span>
      <span className={`tabular ${fuerte ? 'text-lg font-semibold' : 'text-sm'} ${tono ?? ''}`}>{valor}</span>
    </div>
  );
}

/** A difference as the shop says it: «Cuadra», «Sobran C$ 50.00», «Faltan US$ 2.00». */
function Diferencia({ diferencia, formato = formatMoney }: { diferencia: number; formato?: (c: Centavos) => string }) {
  const t = useT();
  if (diferencia === 0) return <p className="font-semibold text-emerald-300">{t('efectivo.cuadra')}</p>;
  return (
    <p className={`font-semibold ${diferencia < 0 ? 'text-rose-300' : 'text-amber-200'}`}>
      {t(diferencia < 0 ? 'efectivo.faltan' : 'efectivo.sobran', { monto: formato(centavos(Math.abs(diferencia))) })}
    </p>
  );
}

/**
 * Receiving what a till handed over, counted blind: the amounts it handed over appear only once
 * the count is in, for the same reason the corte hides what it expects.
 */
function Recibir({
  sesion,
  tasa,
  nombre,
  onCerrar,
}: {
  sesion: SesionCaja;
  tasa: number;
  nombre: string;
  onCerrar: () => void;
}) {
  const t = useT();
  const [cordobas, setCordobas] = useState('');
  const [dolares, setDolares] = useState('');
  const [hecho, setHecho] = useState<{ contadoC: Centavos; contadoD: Centavos; eventoId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explicando, setExplicando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /**
   * What the till handed over, as it was when this count began: read again before it is saved, as
   * «Cuadrar la semana» does. The result was worked out against the till as the sheet opened, and
   * said even when the books refused it (2026-10-13).
   */
  const [esta, setEsta] = useState(sesion);
  const cC = cordobas.trim() ? parseMoney(cordobas) : centavos(0);
  const cD = dolares.trim() ? parseMoney(dolares) : centavos(0);
  const valido = cC !== null && cC >= 0 && cD !== null && cD >= 0 && (cordobas.trim() !== '' || dolares.trim() !== '');
  const entrego = {
    cordobas: centavos(esta.porRecibir ?? 0),
    dolares: centavos(esta.porRecibirDolares ?? 0),
  };

  async function recibir() {
    if (!valido || ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      const viva = await db.sesiones.get(sesion.id);
      if (!viva || ((viva.porRecibir ?? 0) === 0 && (viva.porRecibirDolares ?? 0) === 0)) {
        setError(t('efectivo.yaRecibida'));
        return;
      }
      // It handed over more since the count began: counted blind again, against what it is now.
      if (
        (viva.porRecibir ?? 0) !== (esta.porRecibir ?? 0) ||
        (viva.porRecibirDolares ?? 0) !== (esta.porRecibirDolares ?? 0) ||
        (viva.porRecibirValor ?? 0) !== (esta.porRecibirValor ?? 0)
      ) {
        setEsta(viva);
        setCordobas('');
        setDolares('');
        setError(t('efectivo.cambioLoEntregado'));
        return;
      }
      const { id, rechazo } = await emitirYVer('efectivo.recibido', { sesionId: sesion.id, contadoCordobas: cC!, contadoDolares: cD! });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setHecho({ contadoC: cC!, contadoD: cD!, eventoId: id });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('efectivo.noSeGuardo'));
    } finally {
      setOcupado(false);
    }
  }

  if (hecho) {
    // As the books take it: what the dollars' rate did is not the till's (`alRecibir`).
    const { diferencia } = alRecibir(esta, { cordobas: hecho.contadoC, dolares: hecho.contadoD }, fxRate(tasa));
    return (
      <Modal
        titulo={t('efectivo.recibido')}
        onCerrar={onCerrar}
        pie={
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        }
      >
        <div className="flex flex-col gap-2">
          <Fila etiqueta={t('efectivo.entrego')} valor={formatMoney(entrego.cordobas)} />
          {entrego.dolares > 0 && <Fila etiqueta="" valor={`+ ${enDolares(entrego.dolares)}`} />}
          <Fila etiqueta={t('efectivo.contado')} valor={formatMoney(hecho.contadoC)} />
          {hecho.contadoD > 0 && <Fila etiqueta="" valor={`+ ${enDolares(hecho.contadoD)}`} />}
          <div className="mt-2 border-t border-slate-800 pt-3">
            <Diferencia diferencia={diferencia} />
            {diferencia < 0 && <p className="mt-1 text-xs text-slate-400">{t('efectivo.faltanteDeCaja', { nombre })}</p>}
            {diferencia < 0 && (
              <button type="button" className="btn-secundario mt-3 w-full" onClick={() => setExplicando(true)} data-efectivo-explicar>
                <Ico icono="gastos" />
                {t('explicar.botonFaltante')}
              </button>
            )}
          </div>
        </div>
        {explicando && <ExplicarFaltante eventoId={hecho.eventoId} nombre={nombre} onCerrar={() => setExplicando(false)} />}
      </Modal>
    );
  }

  return (
    <Modal
      titulo={t('efectivo.recibirDe', { nombre })}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!valido || ocupado} onClick={() => void recibir()}>
          <Ico icono="ingresar" />
          {t('efectivo.recibir')}
        </button>
      }
    >
      <p className="mb-4 rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-400">{t('efectivo.recibirAyuda')}</p>
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('caja.cordobasContados')}
          inputMode="decimal"
          className="tabular text-lg"
          value={cordobas}
          onChange={(e) => setCordobas(e.target.value)}
          autoFocus
        />
        <Campo
          etiqueta={t('caja.dolaresContados')}
          inputMode="decimal"
          className="tabular text-lg"
          value={dolares}
          onChange={(e) => setDolares(e.target.value)}
          ayuda={t('caja.seValoranA', { tasa: formatFxRate(tasa as never) })}
        />
        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Counting general cash against the books, dollars at the official rate. */
function Arqueo({
  esperado: esperadoAhora,
  porRecibir,
  tasa,
  onCerrar,
}: {
  esperado: Centavos;
  porRecibir: Centavos;
  tasa: number;
  onCerrar: () => void;
}) {
  const t = useT();
  // The books as the count found them: once saved they say what was counted, and the sheet still
  // has the difference to show.
  const [congelado, setCongelado] = useState<Centavos | null>(null);
  const esperado = congelado ?? esperadoAhora;
  const [cordobas, setCordobas] = useState('');
  const [dolares, setDolares] = useState('');
  const [hecho, setHecho] = useState(false);
  /** The count saved, to explain what it found short. */
  const [arqueoId, setArqueoId] = useState<string | null>(null);
  const [explicando, setExplicando] = useState(false);
  /**
   * The safe never counted before: what it holds is where it starts, against capital, not a
   * surplus (`inicial`), as in «Cuadrar la semana». Read before the count is saved, which ends it.
   * The books' own mark (`cuentasContadas`), and any count this phone has heard of from before
   * the books kept one.
   */
  const primera = useLiveQuery(async () => {
    if (yaContada(await readSettings(), 'CAJA_FUERTE')) return false;
    const sinEfecto = await leerSinEfecto();
    return !(await db.eventos.where('type').equals('efectivo.arqueo').toArray()).some((e) => tuvoEfecto(e, sinEfecto));
  }, []);
  const [fuePrimera, setFuePrimera] = useState(false);
  const cC = cordobas.trim() ? parseMoney(cordobas) : null;
  const cD = dolares.trim() ? parseMoney(dolares) : centavos(0);
  const valido = cC !== null && cC >= 0 && cD !== null && cD >= 0 && primera !== undefined && enRango(() => usdToCordobas(cD, fxRate(tasa))) !== null;
  const enCordobasD = valido ? enRango(() => usdToCordobas(cD!, fxRate(tasa))) : null;
  const contado = valido && enCordobasD !== null ? cC! + enCordobasD : null;
  const diferencia = contado === null ? null : contado - esperado;

  async function guardar() {
    if (!valido) return;
    setCongelado(esperado);
    setFuePrimera(Boolean(primera));
    const { id } = await emitirYVer('efectivo.arqueo', { contadoCordobas: cC!, contadoDolares: cD!, esperado, ...(primera ? { inicial: true } : {}) });
    setArqueoId(id);
    setHecho(true);
  }

  return (
    <Modal
      titulo={t('efectivo.contar')}
      onCerrar={onCerrar}
      pie={
        hecho ? (
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        ) : (
          <button type="button" className="btn-primario w-full" disabled={!valido} onClick={() => void guardar()}>
            <Ico icono="guardar" />
            {t('efectivo.guardarArqueo')}
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <p className="rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-400">{t('efectivo.arqueoAyuda')}</p>
        {(hecho ? fuePrimera : primera) && (
          <p className="rounded-lg bg-marca-500/10 px-3 py-2 text-xs text-marca-200" data-efectivo-arqueo-primera>
            {t('efectivo.arqueoPrimera')}
          </p>
        )}
        {porRecibir > 0 && (
          <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            {t('efectivo.arqueoPorRecibir', { monto: formatMoney(porRecibir) })}
          </p>
        )}
        <Campo
          etiqueta={t('caja.cordobasContados')}
          inputMode="decimal"
          className="tabular text-lg"
          value={cordobas}
          onChange={(e) => setCordobas(e.target.value)}
          readOnly={hecho}
          autoFocus
        />
        <Campo
          etiqueta={t('caja.dolaresContados')}
          inputMode="decimal"
          className="tabular text-lg"
          value={dolares}
          onChange={(e) => setDolares(e.target.value)}
          readOnly={hecho}
          ayuda={t('caja.seValoranA', { tasa: formatFxRate(tasa as never) })}
        />
        <div className="flex flex-col gap-1 border-t border-slate-800 pt-3">
          <Fila etiqueta={t('efectivo.segunLibros')} valor={formatMoney(esperado)} />
          {contado !== null && <Fila etiqueta={t('efectivo.contado')} valor={formatMoney(centavos(contado))} />}
          {diferencia !== null && <Diferencia diferencia={diferencia} />}
          {hecho && <p className="mt-1 text-xs text-slate-400">{t('efectivo.arqueoGuardado')}</p>}
          {/* The first count's difference is where the safe starts, not a shortage. */}
          {hecho && arqueoId && !fuePrimera && diferencia !== null && diferencia < 0 && (
            <button type="button" className="btn-secundario mt-3 w-full" onClick={() => setExplicando(true)} data-efectivo-explicar>
              <Ico icono="gastos" />
              {t('explicar.botonFaltante')}
            </button>
          )}
        </div>
      </div>
      {explicando && arqueoId && <ExplicarFaltante eventoId={arqueoId} onCerrar={() => setExplicando(false)} />}
    </Modal>
  );
}

/**
 * A bank account or the wallet against its statement: the balance it shows, on the date it shows
 * it. The books are read as of that date too, since whatever came in after it is not on the
 * statement. A dollar account's statement is in dollars, held against the dollars the books say it
 * holds; only what it is over or under goes on, at the official rate (`saldoDelEstadoEnDolares`).
 */
function Conciliar({ cuenta, config, onCerrar }: { cuenta: CuentaBanco | 'BILLETERA_MOVIL'; config: Settings; onCerrar: () => void }) {
  const t = useT();
  const enDolar = cuenta !== 'BILLETERA_MOVIL' && esCuentaEnDolares(cuenta);
  const [fecha, setFecha] = useState(hoy());
  const alDia = useLiveQuery(async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return undefined;
    const codigo = ACCOUNTS[cuenta].code;
    return {
      cordobas: await localReader.saldoCuenta(codigo, fecha),
      dolares: enDolar ? await localReader.saldoCuentaDolares(codigo, fecha) : centavos(0),
    };
  }, [cuenta, fecha, enDolar]);
  /*
   * What waits in the queue up to the statement's day, for this kind of account: on the statement,
   * it is money the books do not have here yet. Squared without it, the difference takes it in, and
   * confirming it after puts it in again (2026-10-20); so it is said first, with the way to the queue.
   */
  const enCola = useLiveQuery(async () => {
    if (enDolar || !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return [];
    const forma = cuenta === 'BILLETERA_MOVIL' ? 'billetera' : 'transferencia';
    return (await pagosEnCola()).filter((x) => x.forma === forma && x.fecha <= fecha);
  }, [cuenta, fecha, enDolar], []);
  // As the statement found them, for the same reason as the count.
  const [congelado, setCongelado] = useState<{ cordobas: Centavos; dolares: Centavos } | null>(null);
  const libros = congelado ?? alDia;
  const [saldo, setSaldo] = useState('');
  const [hecho, setHecho] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const monto = saldo.trim() ? parseMoney(saldo) : null;
  const diferencia = monto === null || libros === undefined ? null : monto - (enDolar ? libros.dolares : libros.cordobas);
  const valido = monto !== null && libros !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(fecha) && fecha <= hoy();
  const formato = (c: Centavos) => (enDolar ? enDolares(c) : formatMoney(c));
  const nombrada = cuenta === 'BILLETERA_MOVIL' ? null : nombrePuesto(config, cuenta);

  async function guardar() {
    if (!valido) return;
    setCongelado(libros!);
    setError(null);
    const { rechazo } = await emitirYVer('cuenta.conciliada', {
      cuenta,
      fecha,
      saldo: enDolar
        ? saldoDelEstadoEnDolares({ libros: libros!.cordobas, librosDolares: libros!.dolares, estadoDolares: monto!, tasa: fxRate(config.fxTasaOficial) })
        : monto!,
      ...(enDolar ? { saldoDolares: monto! } : {}),
    });
    if (rechazo) {
      setCongelado(null);
      setError(rechazo);
      return;
    }
    setHecho(true);
  }

  return (
    <Modal
      // The owner's name for it goes in the sheet, whole: a title is cut to one line.
      titulo={
        nombrada
          ? t('efectivo.conciliar')
          : t(cuenta === 'BILLETERA_MOVIL' ? 'efectivo.conciliarBilletera' : enDolar ? 'efectivo.conciliarDolares' : 'efectivo.conciliarBanco')
      }
      onCerrar={onCerrar}
      pie={
        hecho ? (
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        ) : (
          <button type="button" className="btn-primario w-full" disabled={!valido} onClick={() => void guardar()} data-conciliar-guardar>
            <Ico icono="guardar" />
            {t('efectivo.conciliar')}
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4" data-conciliar={cuenta}>
        {nombrada && (
          <p className="font-medium break-words" data-conciliar-cuenta>
            {nombrada}
          </p>
        )}
        <p className="rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-400">
          {t(enDolar ? 'efectivo.conciliarAyudaDolares' : 'efectivo.conciliarAyuda')}
        </p>
        {enCola.length > 0 && !hecho && (
          <div className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-conciliar-en-cola>
            <p>
              {t('efectivo.conciliarEnCola', {
                n: enCola.length,
                monto: formatMoney(enCola.reduce((a, x) => a + x.monto, 0) as Centavos),
              })}
            </p>
            <Link to="/transferencias" className="mt-1.5 inline-flex items-center gap-1.5 font-medium text-amber-100 underline hover:text-white">
              <Ico icono="transferencias" className="size-4 shrink-0" />
              {t('efectivo.verLaCola')}
            </Link>
          </div>
        )}
        <Campo
          etiqueta={t('efectivo.fechaEstado')}
          type="date"
          value={fecha}
          max={hoy()}
          onChange={(e) => setFecha(e.target.value)}
          readOnly={hecho}
        />
        <Campo
          etiqueta={t(enDolar ? 'efectivo.saldoEstadoDolares' : 'efectivo.saldoEstado')}
          inputMode="decimal"
          className="tabular text-lg"
          value={saldo}
          onChange={(e) => setSaldo(e.target.value)}
          readOnly={hecho}
          autoFocus
        />
        <div className="flex flex-col gap-1 border-t border-slate-800 pt-3">
          {libros !== undefined && (
            <Fila
              etiqueta={t('efectivo.segunLibrosAl')}
              valor={enDolar ? `${enDolares(libros.dolares)} · ${formatMoney(libros.cordobas)}` : formatMoney(libros.cordobas)}
            />
          )}
          {diferencia !== null && <Diferencia diferencia={diferencia} formato={formato} />}
          {error && <p className="text-sm text-rose-400">{error}</p>}
          {hecho && (
            <p className="mt-1 text-xs text-slate-400">
              {t('efectivo.conciliado', {
                fecha: new Date(`${fecha}T12:00:00Z`).toLocaleDateString(localeActual(), { timeZone: ZONA_HORARIA }),
              })}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
